import * as zod from 'zod';

import { ReplayTooltip } from './ReplayTooltip';

// Bounds on the playback speed multiplier. Exported so the skill-level speed
// resolved off an untyped feathers row can be held to the same band.
export const MIN_REPLAY_SPEED = 0.1;
export const MAX_REPLAY_SPEED = 2;

// Replay knobs shared by both sources.
const ReplayArgs = zod.object({
  // Move-to-start placement (position-list reference only). Null ⇒ skip
  // move-to-start and anchor wherever the arm is.
  target: zod
    .object({
      positionListID: zod.string(),
      positionListIndex: zod.number().int().nonnegative().optional(),
    })
    .nullable()
    .default(null),
  // Which tooltip the path is replayed on. 'saved' looks up the frame the
  // recording carries; 'active' re-expresses onto the Active Tooltip at run
  // time; an explicit entry names one outright.
  //
  // Undefaulted: absent is every step authored before this field, and those
  // must keep resolving the way they always have ('active'). A default here
  // would rewrite the motion of saved routines nobody edited.
  tooltip: ReplayTooltip.optional(),
  // OFF ⇒ replay in the recorded world frame; `target`/`pin` are ignored.
  repositionEnabled: zod.boolean().default(false),
  // 'start'/'end' land that frame on the target; 'reference' shifts by
  // targetPose · inv(refWorldPose) (requires a recorded reference frame).
  pin: zod.enum(['start', 'end', 'reference']).default('start'),
  // Playback speed multiplier (1.0 = recorded cadence), capped at 2× —
  // speed-ups downsample frames so the arm is commanded at speed× the taught
  // velocity, with arm-control-bot's joint limits as the backstop.
  // Null ⇒ use the skill's shared.speed (null there ⇒ 1.0). Non-null values
  // are overrides: the ephemeral hold-to-replay session sets them so the
  // operator can preview unsaved slider state; legacy saved steps may also
  // carry a numeric speed.
  speed: zod
    .number()
    .min(MIN_REPLAY_SPEED)
    .max(MAX_REPLAY_SPEED)
    .nullable()
    .default(null),
  // Explicit trim window in recorder seconds (frame-timestamp domain).
  // Null ⇒ use the skill's shared trim bounds, falling back to the
  // recording's auto idle-trim. Set only by the ephemeral replay session.
  trimStartTimestamp: zod.number().nullable().default(null),
  trimEndTimestamp: zod.number().nullable().default(null),
});

// Skill-driven source (preferred): the runner replays the skill's newest
// linked recording.
const SkillSource = ReplayArgs.extend({
  skillId: zod.string(),
});

// Legacy direct-recording source — no editor UI, still runnable: just the
// recording id plus the shared replay knobs. (A leftover recordingId on a
// skill-driven step is stripped by the SkillSource parse, which is tried
// first.)
const RecordingSource = ReplayArgs.extend({
  recordingId: zod.string(),
});

export default zod.union([SkillSource, RecordingSource]);
